import { useMemo, useRef } from "react";

/**
 * Derives the same per-pixel min/max envelope `usePeaks` returns, but from an
 * AudioBuffer the browser decoded locally instead of a precomputed .dat.
 *
 * This exists for the create-project page, where the audio has been dropped
 * into the form but not yet uploaded: there is no File entity, so no peaks
 * key, no .dat and nothing for `usePeaks` to fetch. The pre-redesign player
 * drew that page's waveform straight off `buffer.getChannelData(0)` for the
 * same reason, so this is the old behaviour preserved, reshaped to the
 * resample(width) contract WaveformLane already consumes.
 *
 * Returns `null` when there is no buffer, so the lane can treat "no local
 * source" and "no server source" the same way.
 */
export const useBufferPeaks = (buffer?: AudioBuffer | null) => {
  // Memoised per (buffer, width): a full-length min/max scan is O(samples),
  // ~12M iterations for a five-minute track, and the lane calls resample()
  // on every render. Without this cache a resize — or any unrelated parent
  // re-render — would redo the whole scan.
  const cacheRef = useRef<{
    buffer: AudioBuffer;
    width: number;
    peaks: { min: number[]; max: number[] };
  } | null>(null);

  return useMemo(() => {
    if (!buffer) return null;

    return (width: number) => {
      const floorWidth = Math.floor(width);
      if (floorWidth <= 0) return null;

      const cached = cacheRef.current;
      if (cached && cached.buffer === buffer && cached.width === floorWidth) {
        return cached.peaks;
      }

      const samples = buffer.getChannelData(0);
      const min: number[] = [];
      const max: number[] = [];

      for (let i = 0; i < floorWidth; i += 1) {
        const start = Math.floor((i * samples.length) / floorWidth);
        const end = Math.floor(((i + 1) * samples.length) / floorWidth);

        // A column narrower than one sample (a very short clip in a wide
        // lane) would otherwise leave lo/hi at their sentinels and draw a
        // spike to the lane's edges. Fall back to the single nearest sample.
        if (end <= start) {
          const sample = samples[Math.min(start, samples.length - 1)] ?? 0;
          min.push(sample);
          max.push(sample);
          continue;
        }

        let lo = samples[start];
        let hi = samples[start];
        for (let j = start + 1; j < end; j += 1) {
          const sample = samples[j];
          if (sample < lo) lo = sample;
          if (sample > hi) hi = sample;
        }

        min.push(lo);
        max.push(hi);
      }

      const peaks = { min, max };
      cacheRef.current = { buffer, width: floorWidth, peaks };
      return peaks;
    };
  }, [buffer]);
};
